/**
 * Dynamic next-question routing for the legacy recommendation assessment.
 */
import { RECOMMENDATION_QUESTIONS } from '../config/recommendationQuestions';
import { calculateRecommendations } from './recommendationEngine';
import { deriveLoanSizeTier } from './loanSizeTier';

export const LEGACY_CORE_QUESTION_ORDER = [
  'purchaseOrRefinance',
  'refiGoal',
  'propertyLocation',
  'propertyCounty',
  'propertyUse',
  'estimatedValue',
  'downPayment',
  'creditScore',
];

export const LEGACY_REFINEMENT_PRIORITY = [
  'propertyType',
  'employmentStatus',
  'annualIncome',
  'monthlyDebts',
  'militaryService',
  'firstTimeHomeBuyer',
  'stateHfaParticipant',
  'tribalHousingPath',
  'affordableProgramInterest',
  'agencyLoanServicer',
  'refiNowLowIncomeHint',
  'renovationNeeds',
  'incomeDocumentation',
  'nonQmFlexInterest',
  'loanTermPreference',
  'rateTypePreference',
  'planToKeepLoan',
  'refinanceType',
  'ruralProperty',
  'existingUSDALoan',
  'priorNADL',
];

function isAnswered(answers, id) {
  if (id === 'propertyLocation') return hasLocationAnswers(answers);
  const v = answers[id];
  return v !== undefined && v !== null && String(v).trim() !== '';
}

export function hasLocationAnswers(answers) {
  return isAnswered(answers, 'propertyState') && isAnswered(answers, 'propertyCounty');
}

export function hasCoreLegacyAnswers(answers) {
  const tier = deriveLoanSizeTier(answers) || answers.loanSizeTier;
  return (
    isAnswered(answers, 'purchaseOrRefinance')
    && hasLocationAnswers(answers)
    && isAnswered(answers, 'propertyUse')
    && isAnswered(answers, 'estimatedValue')
    && isAnswered(answers, 'downPayment')
    && isAnswered(answers, 'creditScore')
    && Boolean(tier)
    && (answers.purchaseOrRefinance === 'purchase' || isAnswered(answers, 'refiGoal'))
  );
}

export function getVisibleRecommendationQuestions(answers) {
  return RECOMMENDATION_QUESTIONS.filter((q) => {
    if (q.internal) return false;
    if (!q.showIf) return true;
    return q.showIf(answers);
  });
}

export function pickNextLegacyQuestionId(answers) {
  const visible = getVisibleRecommendationQuestions(answers);
  const unanswered = visible.filter((q) => !isAnswered(answers, q.id));
  if (!unanswered.length) return null;

  const coreNext = LEGACY_CORE_QUESTION_ORDER
    .map((id) => unanswered.find((q) => q.id === id))
    .find(Boolean);
  if (coreNext) return coreNext.id;

  if (!hasCoreLegacyAnswers(answers)) return unanswered[0].id;

  const recs = calculateRecommendations(answers);
  if (recs.length <= 1) {
    const byPriority = LEGACY_REFINEMENT_PRIORITY
      .map((id) => unanswered.find((q) => q.id === id))
      .filter(Boolean);
    return (byPriority[0] || unanswered[0]).id;
  }

  const byPriority = LEGACY_REFINEMENT_PRIORITY
    .map((id) => unanswered.find((q) => q.id === id))
    .filter(Boolean);
  return (byPriority[0] || unanswered[0]).id;
}

export function isLegacyAssessmentComplete(answers) {
  return pickNextLegacyQuestionId(answers) === null;
}

export function getLegacyQuestionById(id) {
  return RECOMMENDATION_QUESTIONS.find((q) => q.id === id) || null;
}
